// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  getAdminStats, getAdminUsers,
  getConsommations, exportConsommations,
  getResultatsAnalyses, exportResultatsAnalyses,
} from '../services/api';
import { useI18n } from '../i18n';
import SkeletonRows from '../components/SkeletonRows';
import SortableFilterableTable from '../components/SortableFilterableTable';
import { PALIERS_HISTORIQUE, libellePalier } from '../lib/paliers';

// ⚠️ Liste d'affichage uniquement : `callTypeLabel` retombe sur le code brut si
// le type est absent d'ici. Un nouveau type n'est donc jamais perdu — il
// s'affiche seulement sans libellé.
// ⛔ Le back, lui, FILTRE sur `event_type` (`_fetch_consommations_stats`) : y
// figurer ne suffit pas à s'afficher. `rag_coherence_externe` était listé ici
// depuis toujours sans jamais remonter, faute d'être dans ce filtre (corrigé
// le 24/08/2026). Ajouter un type ⇒ vérifier les DEUX côtés.
// `zpso` = passe dédiée au rattachement des prélèvements aux ZPSO (27/07/2026).
// `contextuel_interne` = analyse contextuelle sur le programme lu DANS le
// rapport, quand aucun projet n'est attaché (24/08/2026).
const CALL_TYPES = ['pass1', 'pass1_retry', 'pass2_metadata', 'pass2_samples', 'pass2_conclusions',
  'pass2_sections', 'pass2_global', 'annexe', 'contextuel_interne', 'rag_coherence_externe',
  'rag_ingestion_think', 'rag_ingestion_extract', 'zpso', 'ocr', 'non_categorise'];

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 };

const ConsommationsSection = () => {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [scope, setScope] = useState('total');
  const [userId, setUserId] = useState('');
  const [tierCode, setTierCode] = useState('PRO');
  const [callTypeFilter, setCallTypeFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { getAdminUsers().then(d => setUsers(d.users || [])).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true); setError('');
    try {
      setData(await getConsommations({
        scope,
        userId: scope === 'user' ? userId : undefined,
        tierCode: scope === 'tier' ? tierCode : undefined,
      }));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope, userId, tierCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const callTypeLabel = (ct) => (CALL_TYPES.includes(ct) ? t(`call_type_${ct}`) : ct);
  const rowsAll = (data?.call_types || []).map(c => ({ ...c, label: callTypeLabel(c.call_type) }));
  const rows = callTypeFilter ? rowsAll.filter(r => r.call_type === callTypeFilter) : rowsAll;

  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--black)' }}>{t('stat_consommations_title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select value={scope} onChange={e => setScope(e.target.value)} style={selectStyle}>
            <option value="total">{t('stat_scope_total')}</option>
            <option value="user">{t('stat_scope_user')}</option>
            <option value="tier">{t('stat_scope_tier')}</option>
          </select>
          {scope === 'user' && (
            <select value={userId} onChange={e => setUserId(e.target.value)} style={selectStyle}>
              <option value="">{t('stat_choose_placeholder')}</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.email}</option>)}
            </select>
          )}
          {scope === 'tier' && (
            <select value={tierCode} onChange={e => setTierCode(e.target.value)} style={selectStyle}>
              {/* Filtre statistique : PRO_PLUS reste, sinon les analyses des
                  comptes qui y sont passés disparaîtraient des mesures. */}
              {PALIERS_HISTORIQUE.map(code => (
                <option key={code} value={code}>{libellePalier(code)}</option>
              ))}
            </select>
          )}
          <select value={callTypeFilter} onChange={e => setCallTypeFilter(e.target.value)} style={selectStyle}>
            <option value="">{t('stat_all_call_types')}</option>
            {rowsAll.map(c => <option key={c.call_type} value={c.call_type}>{c.label}</option>)}
          </select>
          <Button onClick={() => exportConsommations({ scope, userId: scope === 'user' ? userId : undefined, tierCode: scope === 'tier' ? tierCode : undefined }).catch(e => alert(e.message))} variant="outline" size="sm">{t('export_excel')}</Button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('loading_label')}</p>
      ) : error ? (
        <p style={{ color: '#ef4444', fontSize: 13 }}>❌ {error}</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_pages')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{data?.avg_pages ?? '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_token_page_ratio')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{data?.token_page_ratio ?? '—'}</p>
            </div>
          </div>
          <SortableFilterableTable
            hideSearch
            columns={[
              { key: 'label', label: t('stat_col_call_type') },
              { key: 'occurrences', label: t('stat_col_occurrences'), align: 'right' },
              { key: 'avg_tokens_in', label: t('stat_col_tokens_in'), align: 'right' },
              { key: 'avg_tokens_out', label: t('stat_col_tokens_out'), align: 'right' },
            ]}
            rows={rows}
            emptyLabel={t('stat_consommations_empty')}
          />
        </>
      )}
    </div>
  );
};

const ResultatsAnalysesSection = () => {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [tierCode, setTierCode] = useState('');
  const [typeRapport, setTypeRapport] = useState('');
  const [avecProjet, setAvecProjet] = useState('');
  const [societe, setSociete] = useState('');
  const [ruleFilter, setRuleFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { getAdminUsers().then(d => setUsers(d.users || [])).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true); setError('');
    try {
      setData(await getResultatsAnalyses({
        user_id: userId || undefined,
        tier_code: tierCode || undefined,
        type_rapport: typeRapport || undefined,
        avec_projet: avecProjet || undefined,
        societe: societe || undefined,
      }));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId, tierCode, typeRapport, avecProjet, societe]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters = { user_id: userId || undefined, tier_code: tierCode || undefined, type_rapport: typeRapport || undefined, avec_projet: avecProjet || undefined, societe: societe || undefined };
  const typeRapportLabel = (tr) => t(`type_rapport_${tr}`) || tr;

  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--black)' }}>{t('stat_resultats_title')}</h2>
        <Button onClick={() => exportResultatsAnalyses(filters).catch(e => alert(e.message))} variant="outline" size="sm">{t('export_excel')}</Button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={userId} onChange={e => setUserId(e.target.value)} style={selectStyle}>
          <option value="">{t('stat_all_users')}</option>
          {users.map(u => <option key={u.user_id} value={u.user_id}>{u.email}</option>)}
        </select>
        <select value={tierCode} onChange={e => setTierCode(e.target.value)} style={selectStyle}>
          <option value="">{t('stat_all_tiers')}</option>
          {PALIERS_HISTORIQUE.map(code => (
            <option key={code} value={code}>{libellePalier(code)}</option>
          ))}
        </select>
        <select value={typeRapport} onChange={e => setTypeRapport(e.target.value)} style={selectStyle}>
          <option value="">{t('stat_all_type_rapports')}</option>
          {(data?.available_type_rapports || []).map(tr => (
            <option key={tr} value={tr}>{typeRapportLabel(tr)}</option>
          ))}
        </select>
        <select value={avecProjet} onChange={e => setAvecProjet(e.target.value)} style={selectStyle}>
          <option value="">{t('stat_avec_sans_projet')}</option>
          <option value="oui">{t('stat_avec_projet')}</option>
          <option value="non">{t('stat_sans_projet')}</option>
        </select>
        {/* Liste fermée (15/09/2026) : un champ libre laissait saisir une société
            absente de la base et ramenait par `LIKE` des homonymes partiels.
            Les valeurs sont celles du périmètre filtré, cf `available_societes`. */}
        <select value={societe} onChange={e => setSociete(e.target.value)} style={{ ...selectStyle, maxWidth: 220 }}>
          <option value="">{t('stat_all_societes')}</option>
          {(data?.available_societes || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={ruleFilter} onChange={e => setRuleFilter(e.target.value)} style={selectStyle}>
          <option value="">{t('stat_all_rules')}</option>
          {(data?.par_regle || []).map(r => (
            <option key={r.rule_number} value={r.rule_number}>{`R${r.rule_number} — ${r.rule_name}`}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('loading_label')}</p>
      ) : error ? (
        <p style={{ color: '#ef4444', fontSize: 13 }}>❌ {error}</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_nb_rapports')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{data?.nb_rapports ?? 0}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_favorable')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', margin: 0 }}>{data?.avg_favorable ?? '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_reserve')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', margin: 0 }}>{data?.avg_reserve ?? '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('stat_avg_defavorable')}</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', margin: 0 }}>{data?.avg_defavorable ?? '—'}</p>
            </div>
          </div>
          <SortableFilterableTable
            hideSearch
            columns={[
              { key: 'rule_number', label: t('stat_col_rule_number'), align: 'right' },
              { key: 'rule_name', label: t('stat_col_rule_name') },
              { key: 'total', label: t('stat_col_total_avis'), align: 'right' },
              { key: 'taux_favorable', label: t('stat_col_pct_favorable'), align: 'right', render: r => r.taux_favorable != null ? `${r.taux_favorable}%` : '—' },
              { key: 'taux_reserve', label: t('stat_col_pct_reserve'), align: 'right', render: r => r.taux_reserve != null ? `${r.taux_reserve}%` : '—' },
              { key: 'taux_defavorable', label: t('stat_col_pct_defavorable'), align: 'right', render: r => r.taux_defavorable != null ? `${r.taux_defavorable}%` : '—' },
            ]}
            rows={ruleFilter ? (data?.par_regle || []).filter(r => String(r.rule_number) === ruleFilter) : (data?.par_regle || [])}
            emptyLabel={t('stat_resultats_empty')}
          />
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, sub, color, tooltip }) => (
  <div style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, margin: 0 }}>{label}</p>
      {tooltip && (
        <span
          title={tooltip}
          style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--gray)', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', flexShrink: 0, fontWeight: 700 }}
        >?</span>
      )}
    </div>
    <p style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", fontSize: 30, fontWeight: 800, color: color || 'var(--black)', lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>{value}</p>
    {sub && <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</p>}
  </div>
);

const AdminStats = () => {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const { t, lang } = useI18n();
  const formatDate = (d) =>
      new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
  const load = async () => {
    setLoading(true); setError('');
    try { const data = await getAdminStats(); setStats(data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const formatDuration = (sec) => {
    if (!sec) return '-';
    if (sec < 60) return `${Math.round(sec)}s`;
    return `${Math.round(sec / 60)}min ${Math.round(sec % 60)}s`;
  };

  const maxWeekCount = stats?.analyses_by_week ? Math.max(...stats.analyses_by_week.map(w => w.count), 1) : 1;

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)' }}>📈 {t('admin_stats_title')}</h1>
          <Button onClick={load} variant="outline" size="sm">{t('btn_refresh')}</Button>
        </div>

        {loading ? (
          <div style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody><SkeletonRows cols={10} rows={5} /></tbody>
            </table>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {error}</div>
        ) : stats && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
              <StatCard label={t('stat_total')}        value={stats.total_analyses}     color="var(--blue)" />
              <StatCard label={t('stat_completed')}    value={stats.completed_analyses} color="#22c55e" sub={`${stats.success_rate}% ${t('stat_success_pct')}`} />
              <StatCard label={t('stat_error')}        value={stats.error_analyses}     color="#ef4444" />
              <StatCard label={t('stat_in_progress')}  value={stats.in_progress}        color="#f59e0b" />
              <StatCard label={t('stat_avg_score')}    value={stats.avg_score != null ? `${stats.avg_score}/100` : '-'} color="#7c3aed" tooltip={t('stat_avg_score_tooltip')} />
              <StatCard label={t('stat_avg_duration')} value={formatDuration(stats.avg_duration_sec)} />
              <StatCard label={t('stat_users')}        value={stats.total_users}        color="#0891b2" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
              <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px' }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--black)' }}>📅 {t('stat_by_week')}</h2>
                {stats.analyses_by_week.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('no_data')}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {stats.analyses_by_week.map(w => (
                      <div key={w.week} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 68 }}>
                          {`${t('week_prefix')}${w.week.split('-')[1]} ${w.week.split('-')[0]}`}
                        </span>
                        <div style={{ flex: 1, height: 18, background: 'var(--white)', borderRadius: 99, overflow: 'hidden', border: '1px solid var(--gray)' }}>
                          <div style={{ height: '100%', width: `${Math.round((w.count / maxWeekCount) * 100)}%`, background: 'var(--blue)', borderRadius: 99, transition: 'width 0.5s' }} />
                        </div>
                        <span style={{ fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 13, fontWeight: 700, color: 'var(--black)', minWidth: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{w.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px' }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--black)' }}>🏆 {t('stat_top_users')}</h2>
                {stats.top_users.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('no_data')}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {stats.top_users.map((u, i) => (
                      <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: i === 0 ? '#f59e0b' : 'var(--text-muted)', minWidth: 20 }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                        </span>
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                        <span style={{ fontFamily: "'Inter', -apple-system, sans-serif", fontSize: 13, fontWeight: 700, color: 'var(--blue)', fontVariantNumeric: 'tabular-nums' }}>
                          {u.count} {u.count > 1 ? t('analyses_label_pl') : t('analyses_label')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <ConsommationsSection />
            <ResultatsAnalysesSection />
          </>
        )}
      </main>
    </div>
  );
};

export default AdminStats;